import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText,
  Plus,
  Search,
  Building2,
  X,
  Check,
  Package,
  Wrench,
  ChevronDown,
  ChevronRight,
  Container,
  Download,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  listInvoices,
  fetchInvoiceableItems,
  previewInvoice,
} from "../../store/slices/invoicesSlice";
import { suggestionsAPI } from "../../services/api";
import { useInvoiceExport } from "../../hooks/useInvoiceExport";
import Button from "../../components/UI/Button";
import { Select, Input } from "../../components/UI/FormField";
import { Pagination } from "../../components/ListView/Pagination";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { money, fmtDate, STATUS_BADGE } from "./invoiceUtils";
import { hasTaxCategory, rowTaxAmount } from "../../utils/tax";
import type {
  Invoice,
  InvoiceStatus,
  InvoiceableItem,
  UninvoicedServiceRow,
  ServiceSelections,
} from "../../types/invoice";

// A service row is selectable when it isn't already on a locked invoice, isn't
// held by a draft, and (for works) is actually billable (non-cancelled, qty > 0).
const rowSelectable = (r: UninvoicedServiceRow) =>
  !r.invoiced && !r.inDraft && r.billable !== false;

type CompanyChoice = { id: string; companyName: string };

// ── Reusable "search & select a company" control ─────────────────────────────
const CompanySearch: React.FC<{
  value: CompanyChoice | null;
  onSelect: (c: CompanyChoice | null) => void;
  placeholder: string;
  autoFocus?: boolean;
  className?: string;
}> = ({ value, onSelect, placeholder, autoFocus, className = "" }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    { _id: string; companyName: string }[]
  >([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout>>();

  const search = (q: string) => {
    setQuery(q);
    setOpen(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      if (q.trim().length < 2) {
        setResults([]);
        return;
      }
      try {
        const res = await suggestionsAPI.getPartners({
          query: q.trim(),
          limit: 8,
        });
        setResults(res.data.data.suggestions ?? []);
      } catch {
        setResults([]);
      }
    }, 300);
  };

  if (value) {
    return (
      <div
        className={`inline-flex items-center gap-1.5 h-9 pl-2.5 pr-1.5 text-[13px] bg-[#F5F4F0] border border-[rgba(11,15,14,0.15)] rounded-md ${className}`}
      >
        <Building2 className="h-3.5 w-3.5 text-[#8A9590]" />
        <span className="font-medium text-[#0B0F0E] truncate max-w-[160px]">
          {value.companyName}
        </span>
        <button
          onClick={() => {
            onSelect(null);
            setQuery("");
            setResults([]);
          }}
          className="ml-0.5 text-[#8A9590] hover:text-[#0B0F0E]"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <Search className="h-4 w-4 text-[#8A9590] absolute left-2.5 top-2.5" />
      <input
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => search(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full h-9 pl-8 pr-3 text-[13px] border border-[rgba(11,15,14,0.15)] rounded-md focus:ring-2 focus:ring-[#C97A3C] focus:outline-none"
      />
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[rgba(11,15,14,0.12)] rounded-md shadow-md divide-y divide-[rgba(11,15,14,0.06)] max-h-64 overflow-auto">
          {results.map((r) => (
            <button
              key={r._id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect({ id: r._id, companyName: r.companyName });
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-[13px] hover:bg-[#F5F4F0] flex items-center gap-2"
            >
              <Building2 className="h-4 w-4 text-[#8A9590]" />
              {r.companyName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Creator name from a possibly-populated createdBy field.
const creatorName = (inv: Invoice): string => {
  const c = inv.createdBy;
  if (!c || typeof c === "string") return "—";
  return `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "—";
};

// Distinct source references drawn from the invoice's lines.
type LinkedRefType = "shipment" | "work" | "containerLine";

const linkedRefs = (
  inv: Invoice,
): { label: string; type: LinkedRefType }[] => {
  const seen = new Set<string>();
  const out: { label: string; type: LinkedRefType }[] = [];
  for (const l of inv.lines || []) {
    const ref = l.shipmentNumber || l.workNumber || l.containerLineNumber;
    if (!ref || seen.has(ref)) continue;
    seen.add(ref);
    // Read off the line's own source type where it has one, so a container
    // reference is not mistaken for a shipment by virtue of not being a work.
    const type: LinkedRefType =
      l.sourceType === "work" || l.workNumber
        ? "work"
        : l.sourceType === "containerLine" || l.containerLineNumber
          ? "containerLine"
          : "shipment";
    out.push({ label: ref, type });
  }
  return out;
};

const InvoicesListPage: React.FC = () => {
  const { t } = useTranslation("invoicing");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    invoices,
    loading,
    pagination,
    invoiceableItems,
    itemsLoading,
    itemsPagination,
    saving,
  } = useAppSelector((s) => s.invoices);

  const [tab, setTab] = useState<"invoices" | "uninvoiced">("invoices");

  // Invoices-tab filters.
  const [status, setStatus] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [company, setCompany] = useState<CompanyChoice | null>(null);
  const [page, setPage] = useState(1);

  // Handed the same filter state the listing effect sends, so the spreadsheet
  // is always the screen it was taken from — every invoice that matches, not
  // the page that happens to be open.
  const { handleExport, exporting } = useInvoiceExport({
    status,
    invoiceType: type,
    clientId: company?.id,
    dateFrom,
    dateTo,
  });

  // Uninvoiced-tab state. Selection is per SERVICE ROW (service-level
  // invoicing): each entry keeps the item snapshot (so selections survive
  // pagination) plus the chosen service-row ids.
  const [itemCompany, setItemCompany] = useState<CompanyChoice | null>(null);
  const [itemsPage, setItemsPage] = useState(1);
  const [selected, setSelected] = useState<
    Map<string, { item: InvoiceableItem; serviceIds: Set<string> }>
  >(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const keyOf = (i: { sourceType: string; id: string }) =>
    `${i.sourceType}:${i.id}`;

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Independent-invoice client picker.
  const [pickerOpen, setPickerOpen] = useState(false);

  const statusText = (s: InvoiceStatus, isCredit: boolean) =>
    isCredit ? t("status.creditNote") : t(`status.${s}`);

  useEffect(() => {
    dispatch(
      listInvoices({
        status: status || undefined,
        invoiceType: type || undefined,
        clientId: company?.id || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        limit: 20,
      }),
    );
  }, [dispatch, status, type, company, dateFrom, dateTo, page]);

  useEffect(() => {
    if (tab === "uninvoiced") {
      dispatch(
        fetchInvoiceableItems({
          clientId: itemCompany?.id || undefined,
          page: itemsPage,
          limit: 20,
        }),
      );
    }
  }, [dispatch, tab, itemCompany, itemsPage]);

  const rows = useMemo(() => invoices, [invoices]);

  const selectedEntries = Array.from(selected.values());
  const selectedRowCount = selectedEntries.reduce(
    (n, e) => n + e.serviceIds.size,
    0,
  );
  const selectedTotal = selectedEntries.reduce(
    (s, e) =>
      s +
      e.item.services
        .filter((r) => e.serviceIds.has(r.serviceId))
        .reduce((t, r) => t + r.total, 0),
    0,
  );
  const distinctClients = new Set(
    selectedEntries.map((e) => e.item.client.id).filter(Boolean),
  );

  // Item-level checkbox: select/clear every selectable service row of the item.
  const toggleItem = (item: InvoiceableItem) => {
    if (!item.invoiceable) return;
    setSelected((prev) => {
      const next = new Map(prev);
      const k = keyOf(item);
      const all = item.services.filter(rowSelectable).map((r) => r.serviceId);
      const current = next.get(k);
      if (current && current.serviceIds.size === all.length) next.delete(k);
      else next.set(k, { item, serviceIds: new Set(all) });
      return next;
    });
  };

  const toggleService = (item: InvoiceableItem, row: UninvoicedServiceRow) => {
    if (!rowSelectable(row)) return;
    setSelected((prev) => {
      const next = new Map(prev);
      const k = keyOf(item);
      const ids = new Set(next.get(k)?.serviceIds ?? []);
      ids.has(row.serviceId) ? ids.delete(row.serviceId) : ids.add(row.serviceId);
      if (ids.size) next.set(k, { item, serviceIds: ids });
      else next.delete(k);
      return next;
    });
  };

  // Build a preview and jump to the editor.
  const goToEditor = async (
    clientId: string,
    shipmentIds: string[],
    workIds: string[],
    containerLineIds: string[],
    serviceSelections?: ServiceSelections,
  ) => {
    const res = await dispatch(
      previewInvoice({
        clientId,
        shipmentIds,
        workIds,
        containerLineIds,
        serviceSelections,
      }),
    );
    if (!previewInvoice.fulfilled.match(res)) {
      toast.error((res.payload as string) || t("editor.saveFailed"));
      return;
    }
    if (!res.payload.invoicingReadiness?.ready) {
      // Named, so a company stopped by a newly required setting (the invoice
      // number range) is told which one rather than left to hunt for it.
      const missing = res.payload.invoicingReadiness?.missing ?? [];
      toast.error(
        missing.length
          ? t("clientProfile.completeSettingsMissing", {
              missing: missing
                .map((m: string) => t(`readinessLabels.${m}`, { defaultValue: m }))
                .join(", "),
            })
          : t("clientProfile.completeSettingsFirst"),
      );
      return;
    }
    const qs = new URLSearchParams({ clientId });
    if (shipmentIds.length) qs.set("shipmentIds", shipmentIds.join(","));
    if (workIds.length) qs.set("workIds", workIds.join(","));
    if (containerLineIds.length)
      qs.set("containerLineIds", containerLineIds.join(","));
    navigate(`/invoices/new?${qs.toString()}`);
  };

  // From the selection on the Uninvoiced tab — per-service-row selection is
  // passed through so only the picked rows become invoice lines.
  const newFromSelection = async () => {
    if (selectedRowCount === 0) return;
    if (distinctClients.size > 1) {
      toast.error(t("items.multiClientError"));
      return;
    }
    const clientId = selectedEntries[0].item.client.id;
    if (!clientId) {
      toast.error(t("items.multiClientError"));
      return;
    }
    const shipmentIds: string[] = [];
    const workIds: string[] = [];
    const containerLineIds: string[] = [];
    const serviceSelections: ServiceSelections = {};
    for (const e of selectedEntries) {
      if (!e.serviceIds.size) continue;
      const bucket =
        e.item.sourceType === "work"
          ? workIds
          : e.item.sourceType === "containerLine"
            ? containerLineIds
            : shipmentIds;
      bucket.push(e.item.id);
      serviceSelections[e.item.id] = [...e.serviceIds];
    }
    await goToEditor(
      clientId,
      shipmentIds,
      workIds,
      containerLineIds,
      serviceSelections,
    );
  };

  // The header "New invoice" button. Selection → build from it; otherwise open
  // the client picker for an independent (source-less) invoice.
  const handleNewInvoice = () => {
    if (selectedRowCount > 0) {
      newFromSelection();
    } else {
      setPickerOpen(true);
    }
  };

  const th = "text-left font-medium px-3 py-2 whitespace-nowrap";

  // Shared cell renderers so the desktop table and the mobile cards stay in sync.
  const renderStatus = (inv: Invoice) => (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
        STATUS_BADGE[inv.status as InvoiceStatus]
      }`}
    >
      {statusText(inv.status as InvoiceStatus, inv.invoiceType === "credit_note")}
    </span>
  );

  const renderLinked = (inv: Invoice) => {
    const refs = linkedRefs(inv);
    if (refs.length === 0) return <span className="text-[#b6b6b1]">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {refs.slice(0, 2).map((r) => (
          <span
            key={r.label}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
              r.type === "work"
                ? "bg-[#EAF3DE] text-[#27500A]"
                : r.type === "containerLine"
                  ? "bg-[#E2EFEC] text-[#075049]"
                  : "bg-[#E6F1FB] text-[#0C447C]"
            }`}
          >
            {r.type === "work" ? (
              <Wrench className="h-2.5 w-2.5" />
            ) : r.type === "containerLine" ? (
              <Container className="h-2.5 w-2.5" />
            ) : (
              <Package className="h-2.5 w-2.5" />
            )}
            {r.label}
          </span>
        ))}
        {refs.length > 2 && (
          <span className="text-[10px] text-[#8A9590]">+{refs.length - 2}</span>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-[#C97A3C] shrink-0" />
          <div>
            <h1 className="text-xl font-medium text-[#0B0F0E]">
              {t("list.title")}
            </h1>
            <p className="text-[13px] text-[#8A9590]">{t("list.subtitle")}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {/* Only on the invoices tab — the uninvoiced tab is a worklist of
              things that have no invoice to export yet. */}
          {tab === "invoices" && (
            <Button
              variant="secondary"
              onClick={handleExport}
              disabled={exporting}
              className="w-full sm:w-auto justify-center"
            >
              <Download className="h-4 w-4 mr-1" />{" "}
              {exporting ? t("list.exporting") : t("list.export")}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleNewInvoice}
            disabled={saving}
            className="w-full sm:w-auto justify-center"
          >
            <Plus className="h-4 w-4 mr-1" />{" "}
            {selectedRowCount > 0
              ? t("items.newInvoiceFromSelection", { count: selectedRowCount })
              : t("list.newInvoice")}
          </Button>
        </div>
      </div>

      {/* Independent-invoice client picker */}
      {pickerOpen && (
        <div className="bg-white rounded-md border border-[rgba(11,15,14,0.1)] shadow-sm p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-medium text-[#0B0F0E]">
              {t("items.pickClientTitle")}
            </span>
            <button onClick={() => setPickerOpen(false)}>
              <X className="h-4 w-4 text-[#8A9590]" />
            </button>
          </div>
          <p className="text-[12px] text-[#8A9590] mb-2">
            {t("items.pickClientHint")}
          </p>
          <CompanySearch
            value={null}
            autoFocus
            placeholder={t("list.searchClients")}
            onSelect={(c) => {
              if (c) {
                setPickerOpen(false);
                goToEditor(c.id, [], [], []);
              }
            }}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[rgba(11,15,14,0.1)]">
        {(["invoices", "uninvoiced"] as const).map((tk) => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${
              tab === tk
                ? "border-[#C97A3C] text-[#0B0F0E]"
                : "border-transparent text-[#8A9590] hover:text-[#0B0F0E]"
            }`}
          >
            {tk === "invoices" ? t("list.tabInvoices") : t("list.tabUninvoiced")}
          </button>
        ))}
      </div>

      {tab === "invoices" ? (
        <>
          {/* Invoice filters — 2-col grid on phone/tablet, single row on lg+.
              (A single flex row cramps and overlaps at mid widths.) */}
          <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center">
            <Select
              size="sm"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
              placeholder={t("list.allTypes")}
              options={[
                { value: "invoice", label: t("list.typeInvoices") },
                { value: "credit_note", label: t("list.typeCreditNotes") },
              ]}
              className="w-full lg:w-[150px]"
            />
            <Select
              size="sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              placeholder={t("list.allStatuses")}
              options={[
                { value: "draft", label: t("status.draft") },
                { value: "sent", label: t("status.sent") },
                { value: "paid", label: t("status.paid") },
                { value: "overdue", label: t("status.overdue") },
                { value: "cancelled", label: t("status.cancelled") },
              ]}
              className="w-full lg:w-[150px]"
            />
            <div className="col-span-2 lg:col-span-1 flex items-center gap-1.5">
              <span className="text-[12px] text-[#8A9590] shrink-0">
                {t("list.filterFrom")}
              </span>
              <Input
                size="sm"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="flex-1 min-w-0 lg:flex-none lg:w-[150px]"
              />
              <span className="text-[12px] text-[#8A9590] shrink-0">
                {t("list.filterTo")}
              </span>
              <Input
                size="sm"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="flex-1 min-w-0 lg:flex-none lg:w-[150px]"
              />
            </div>
            <CompanySearch
              value={company}
              placeholder={t("list.filterCompany")}
              onSelect={(c) => {
                setCompany(c);
                setPage(1);
              }}
              className="col-span-2 lg:col-span-1 w-full lg:w-[220px]"
            />
          </div>

          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)]">
            {/* Desktop / tablet: table. Column order reads identity → state →
                what it bills → timeline → owner → amount (money last, right). */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#F5F4F0] text-[#8A9590]">
                  <tr>
                    <th className={th}>{t("list.colNumber")}</th>
                    <th className={th}>{t("list.colStatus")}</th>
                    <th className={th}>{t("list.colLinked")}</th>
                    <th className={th}>{t("list.colIssued")}</th>
                    <th className={th}>{t("list.colDue")}</th>
                    <th className={th}>{t("list.colCreatedBy")}</th>
                    <th className={`${th} text-right`}>{t("list.colTotal")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-12">
                        <div className="flex justify-center">
                          <LoadingSpinner size="md" />
                        </div>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-6 text-center text-[#8A9590]"
                      >
                        {t("list.empty")}
                      </td>
                    </tr>
                  ) : (
                    rows.map((inv) => (
                      <tr
                        key={inv._id}
                        onClick={() => navigate(`/invoices/${inv._id}`)}
                        className="border-t border-[rgba(11,15,14,0.06)] hover:bg-[#F8F8F6] cursor-pointer"
                      >
                        <td className="px-3 py-2 font-medium whitespace-nowrap">
                          {inv.invoiceNumber}
                          <span className="ml-2 text-[11px] text-[#8A9590]">
                            {inv.clientSnapshot?.companyName}
                          </span>
                        </td>
                        <td className="px-3 py-2">{renderStatus(inv)}</td>
                        <td className="px-3 py-2">{renderLinked(inv)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {fmtDate(inv.invoiceDate)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {inv.invoiceType === "credit_note"
                            ? "—"
                            : fmtDate(inv.dueDate)}
                        </td>
                        <td className="px-3 py-2 text-[#6b6b67] whitespace-nowrap">
                          {creatorName(inv)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-medium whitespace-nowrap ${
                            inv.invoiceType === "credit_note"
                              ? "text-[#A32D2D]"
                              : ""
                          }`}
                        >
                          {money(inv.total)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards. */}
            <div className="md:hidden divide-y divide-[rgba(11,15,14,0.06)]">
              {loading ? (
                <div className="py-12 flex justify-center">
                  <LoadingSpinner size="md" />
                </div>
              ) : rows.length === 0 ? (
                <div className="px-4 py-6 text-center text-[#8A9590]">
                  {t("list.empty")}
                </div>
              ) : (
                rows.map((inv) => (
                  <button
                    key={inv._id}
                    onClick={() => navigate(`/invoices/${inv._id}`)}
                    className="w-full text-left px-4 py-3 hover:bg-[#F8F8F6] active:bg-[#F1EFE8]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-[#0B0F0E]">
                          {inv.invoiceNumber}
                        </div>
                        <div className="text-[12px] text-[#8A9590] truncate">
                          {inv.clientSnapshot?.companyName}
                        </div>
                      </div>
                      {renderStatus(inv)}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-[#8A9590]">
                        {t("list.colIssued")} {fmtDate(inv.invoiceDate)}
                        {inv.invoiceType !== "credit_note" && (
                          <>
                            {" · "}
                            {t("list.colDue")} {fmtDate(inv.dueDate)}
                          </>
                        )}
                      </div>
                      <div
                        className={`text-[15px] font-medium ${
                          inv.invoiceType === "credit_note"
                            ? "text-[#A32D2D]"
                            : "text-[#0B0F0E]"
                        }`}
                      >
                        {money(inv.total)}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      {renderLinked(inv)}
                      <span className="text-[11px] text-[#8A9590] shrink-0">
                        {creatorName(inv)}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
            <Pagination
              pagination={{
                page: pagination.page,
                pages: pagination.pages,
                total: pagination.total,
                hasNext: pagination.page < pagination.pages,
                hasPrev: pagination.page > 1,
              }}
              onPageChange={(p) => setPage(p)}
              pageSize={pagination.limit || 20}
            />
          </div>
        </>
      ) : (
        <>
          {/* Uninvoiced filters */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2">
            <CompanySearch
              value={itemCompany}
              placeholder={t("list.filterCompany")}
              onSelect={(c) => {
                setItemCompany(c);
                setItemsPage(1);
              }}
              className="w-full sm:w-[240px]"
            />
            {selectedRowCount > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <span className="text-[13px] text-[#8A9590]">
                  {t("items.selectedServicesSummary", {
                    count: selectedRowCount,
                    amount: money(selectedTotal),
                  })}
                </span>
                {distinctClients.size > 1 && (
                  <span className="text-[12px] text-[#A32D2D]">
                    {t("items.multiClientWarning")}
                  </span>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  disabled={saving || distinctClients.size > 1}
                  onClick={newFromSelection}
                  className="w-full sm:w-auto justify-center"
                >
                  {t("items.newInvoiceFromSelection", {
                    count: selectedRowCount,
                  })}
                </Button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)]">
            {itemsLoading ? (
              <div className="py-12 flex justify-center">
                <LoadingSpinner size="md" />
              </div>
            ) : invoiceableItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-[#8A9590]">
                {t("items.empty")}
              </div>
            ) : (
              <div className="divide-y divide-[rgba(11,15,14,0.06)]">
                {invoiceableItems.map((it) => {
                  const k = keyOf(it);
                  const entry = selected.get(k);
                  const pickedCount = entry?.serviceIds.size ?? 0;
                  const selectableRows = it.services.filter(rowSelectable);
                  const allOn =
                    selectableRows.length > 0 &&
                    pickedCount === selectableRows.length;
                  const isOpen = expanded.has(k);
                  return (
                    <div key={k}>
                      {/* ── One row per source, whatever kind it is ─────────
                          Containers list flat, exactly as shipments and works
                          do. They were briefly grouped under their container
                          order, and that could not work here: this list is
                          sorted newest-first across every source, so keeping a
                          booking's containers contiguous meant either
                          re-sorting the whole list around them or hoisting rows
                          out of order — and the half-built version did neither,
                          leaving a header whose total described rows that were
                          three positions further down.

                          Selecting a whole booking in one action is a real
                          want, and it belongs on the container order's own page
                          where it can act on that order alone. Here, one row is
                          one billable thing. */}
                      <div
                        onClick={() => toggleExpanded(k)}
                        className={`flex items-center gap-3 px-4 py-2.5 text-[13px] cursor-pointer hover:bg-[#F8F8F6] ${
                          it.invoiceable ? "" : "opacity-60"
                        }`}
                      >
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleItem(it);
                          }}
                        >
                          <div
                            className={`h-[18px] w-[18px] rounded border flex items-center justify-center shrink-0 ${
                              allOn || pickedCount > 0
                                ? "bg-[#C97A3C] border-[#C97A3C]"
                                : "border-[#d0cfc8]"
                            } ${it.invoiceable ? "" : "opacity-40"}`}
                          >
                            {allOn && <Check className="h-3 w-3 text-white" />}
                            {!allOn && pickedCount > 0 && (
                              <div className="h-0.5 w-2.5 bg-white rounded" />
                            )}
                          </div>
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex items-center gap-2 flex-wrap">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                it.sourceType === "work"
                                  ? "bg-[#EAF3DE] text-[#27500A]"
                                  : it.sourceType === "containerLine"
                                    ? "bg-[#E2EFEC] text-[#075049]"
                                    : "bg-[#E6F1FB] text-[#0C447C]"
                              }`}
                            >
                              {it.sourceType === "work"
                                ? t("clientProfile.badgeWork")
                                : it.sourceType === "containerLine"
                                  ? t("clientProfile.badgeContainer")
                                  : t("clientProfile.badgeShipment")}
                            </span>
                            <span>
                              {it.reference} · {it.route || "—"}
                            </span>
                            <span className="text-[11px] text-[#8A9590]">
                              · {it.client.companyName}
                            </span>
                          </div>
                          <div className="text-[11px] text-[#8A9590] flex items-center gap-1.5 flex-wrap mt-0.5">
                            {/* The booking's own reference. The container's
                                number already carries the order (CO007-01), but
                                the reference the client quotes does not appear
                                anywhere else on this row — it used to sit on the
                                group header, and removing that must not lose
                                it. */}
                            {it.order?.referenceNumber && (
                              <>
                                <span>{it.order.referenceNumber}</span>·
                              </>
                            )}
                            <span className="capitalize">{it.stage}</span>·
                            <span>
                              {t("clientProfile.uninvoicedServiceCount", {
                                count: it.uninvoicedCount,
                                total: it.servicesCount,
                              })}
                            </span>
                            {it.invoices.length > 0 ? (
                              <>
                                ·
                                <span className="inline-flex items-center gap-1 text-[#8a5a00]">
                                  {t("items.priorCount", {
                                    count: it.invoices.length,
                                  })}
                                  :{" "}
                                  {it.invoices
                                    .map((iv) => iv.invoiceNumber)
                                    .join(", ")}
                                </span>
                              </>
                            ) : (
                              <>
                                ·<span>{t("items.neverInvoiced")}</span>
                              </>
                            )}
                            {!it.invoiceable && (
                              <>·<span>{t("clientProfile.noBillableServices")}</span></>
                            )}
                          </div>
                        </div>
                        <span className="font-medium whitespace-nowrap">
                          {money(it.subtotal)}
                        </span>
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 text-[#8A9590] shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-[#8A9590] shrink-0" />
                        )}
                      </div>

                      {/* Service rows — the selectable unit (service-level invoicing) */}
                      {isOpen && (
                        <div className="bg-[#FAFAF8] border-t border-[rgba(11,15,14,0.04)]">
                          {it.services.length === 0 ? (
                            <div className="px-11 py-2 text-[12px] text-[#8A9590]">
                              {t("clientProfile.noServices")}
                            </div>
                          ) : (
                            it.services.map((row) => {
                              const canPick = rowSelectable(row);
                              const rowOn = !!entry?.serviceIds.has(
                                row.serviceId,
                              );
                              return (
                                <div
                                  key={row.serviceId}
                                  onClick={() => toggleService(it, row)}
                                  className={`flex items-center gap-3 pl-11 pr-4 py-1.5 text-[12px] ${
                                    canPick
                                      ? "cursor-pointer hover:bg-[#F3F2EE]"
                                      : "opacity-60"
                                  }`}
                                >
                                  <div
                                    className={`h-[18px] w-[18px] rounded border flex items-center justify-center shrink-0 ${
                                      rowOn
                                        ? "bg-[#C97A3C] border-[#C97A3C]"
                                        : "border-[#d0cfc8]"
                                    } ${canPick ? "" : "opacity-40"}`}
                                  >
                                    {rowOn && (
                                      <Check className="h-3 w-3 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0 truncate">
                                    <span className="font-medium">
                                      {row.name}
                                    </span>
                                    {row.description &&
                                      row.description !== row.name && (
                                        <span className="text-[#8A9590]">
                                          {" "}
                                          · {row.description}
                                        </span>
                                      )}
                                    <span className="text-[#8A9590]">
                                      {" "}
                                      · {row.qty} × {money(row.price)}
                                    </span>
                                    {hasTaxCategory(row.tax) && (
                                      <span className="text-[#8A9590]">
                                        {" "}
                                        ·{" "}
                                        {row.tax === "24"
                                          ? t("vat.vat24")
                                          : t("vat.vat0")}{" "}
                                        ({money(rowTaxAmount(row.total, row.tax))}
                                        )
                                      </span>
                                    )}
                                  </div>
                                  {row.invoiced && (
                                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#EEEDE9] text-[#6b6b67] shrink-0">
                                      {t("clientProfile.badgeInvoiced")}
                                    </span>
                                  )}
                                  {/* Priced at nothing. Legal — a waived
                                      charge — but also what a service with no
                                      default rate leaves behind when nobody
                                      sets one, so it is named rather than
                                      billed quietly as €0. Not shown once the
                                      row is invoiced: by then it is a record,
                                      not a decision. */}
                                  {!row.invoiced && Number(row.price) === 0 && (
                                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FDF8EE] text-[#8a6d1f] shrink-0">
                                      {t("clientProfile.badgeNoRate")}
                                    </span>
                                  )}
                                  {row.inDraft && row.draftInvoiceId && (
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/invoices/${row.draftInvoiceId}/edit`,
                                        );
                                      }}
                                      title={t("items.openDraft")}
                                      className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FAEEDA] text-[#8a5a00] shrink-0 cursor-pointer hover:underline"
                                    >
                                      {t("clientProfile.badgeInDraft")}
                                      {row.draftInvoiceNumber
                                        ? ` · ${row.draftInvoiceNumber}`
                                        : ""}
                                    </span>
                                  )}
                                  {!row.invoiced && row.billable === false && (
                                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FBEAEA] text-[#A32D2D] shrink-0">
                                      {t("clientProfile.badgeNotBillable")}
                                    </span>
                                  )}
                                  <span className="font-medium whitespace-nowrap tabular-nums">
                                    {money(row.total)}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <Pagination
              pagination={{
                page: itemsPagination.page,
                pages: itemsPagination.pages,
                total: itemsPagination.total,
                hasNext: itemsPagination.page < itemsPagination.pages,
                hasPrev: itemsPagination.page > 1,
              }}
              onPageChange={(p) => setItemsPage(p)}
              pageSize={itemsPagination.limit || 20}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default InvoicesListPage;
